import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIMES = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];
const label = (t) => new Date(`2000-01-01T${t}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const EMPTY = { name: "", email: "", phone: "", service: "custom_home", address: "", date: "", time: "09:00", notes: "", website: "" };

export const BookVisit = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/appointments`, f);
      setDone(true);
      setF(EMPTY);
      toast.success("Visit requested — we'll confirm shortly.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="book" className="border-t border-[#E6E2DA] bg-white py-16 sm:py-24">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-4">
          <CalendarDays className="mb-4 text-[#A67C00]" size={28} />
          <p className="eyebrow mb-3">Book a visit</p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Pick a day for your on-site estimate.</h2>
          <p className="mt-4 text-[#4A4742]">Choose a date and time that works for you. We'll confirm by phone or email. Signed-in customers also see confirmed visits on their portal calendar.</p>
        </div>
        <form onSubmit={submit} data-testid="book-visit-form" className="grid gap-4 rounded-2xl border border-[#E6E2DA] bg-[#FAF8F5] p-6 sm:grid-cols-2 sm:p-8 lg:col-span-8">
          {done && <p data-testid="book-visit-success" className="rounded-lg bg-green-50 p-3 text-sm font-medium text-green-700 sm:col-span-2">Request received — we'll confirm your visit soon.</p>}
          <Input required placeholder="Full name" value={f.name} onChange={set("name")} data-testid="visit-name-input" />
          <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="visit-email-input" />
          <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="visit-phone-input" />
          <Input placeholder="Job address" value={f.address} onChange={set("address")} data-testid="visit-address-input" />
          <Input required type="date" min={today()} value={f.date} onChange={set("date")} data-testid="visit-date-input" />
          <Select value={f.time} onValueChange={(v) => setF({ ...f, time: v })}>
            <SelectTrigger data-testid="visit-time-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{label(t)}</SelectItem>)}</SelectContent>
          </Select>
          <div className="sm:col-span-2">
            <Select value={f.service} onValueChange={(v) => setF({ ...f, service: v })}>
              <SelectTrigger data-testid="visit-service-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="custom_home">Custom home</SelectItem>
                <SelectItem value="concrete">Concrete work</SelectItem>
                <SelectItem value="remodel">Remodeling</SelectItem>
                <SelectItem value="other">Something else</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={set("website")} />
          <Textarea className="sm:col-span-2" placeholder="Anything we should know?" value={f.notes} onChange={set("notes")} data-testid="visit-notes-input" />
          <button disabled={busy} data-testid="book-visit-submit-btn" className="rounded-full bg-[#0E1F3F] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#F2B91C] hover:text-[#0E1F3F] disabled:opacity-60 sm:col-span-2">
            {busy ? "Sending…" : "Request this visit"}
          </button>
        </form>
      </div>
    </section>
  );
};
